// ANGULAR
import { Injectable } from '@angular/core';

// NGRX
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';

// RXJS
import { Observable, of, throwError } from 'rxjs';
import { catchError, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';

// SERVICES
import { BusinessService } from '../services/business.service';

// ACTIONS AND STATES
import * as globalActions from '../../../global/store/actions';
import { State } from '../../../root-state';
import * as businessActions from './actions';

// COMMON INTERFACES AND ENUMS
import { ModalType } from '../../../../models/common/modal/modal-config';

// CONSTANT
import { BUSINESS_NGRX_CONSTANTS } from '../../../../constants/ngrx-store/business';


/**
 * This effect is for listen business actions, execute business service call and dispatch in business reducer.
 * For business api call purpose.
 *
 * @author Vikas Thakkar <vikas.thakkar@saeculumsolutions.com>
 *
 * Notes:-
 * Date: 16/03/2020 (Vikas Thakkar <vikas.thakkar@saeculumsolutions.com>)  created business effect
 */
@Injectable()
export class BusinessEffects {
  constructor(
    private actions$: Actions,
    private store: Store<State>,
    private businessService: BusinessService) { }

  /**
		  * business Effect
		  * @param type : business action name.
		  * @param payload : object to identify common business method base on module and method.
		  * @returns Empty action dispatch to reducer.
		  */
  businessCommonEffect$ = createEffect(() => this.actions$.pipe(
    ofType<businessActions.BusinessCommonAction>(businessActions.ActionTypes.BUSINESS_COMMON),
    withLatestFrom(this.store),
    switchMap(([action, state]): Observable<any> => {
      // get common constant base action module and method.
      // changed to payload type from payload.data.method
      const common = BUSINESS_NGRX_CONSTANTS[action.payload.module];

      // avoid service call if data already exists in store.
      // avoid service call if data already exists in store.
      if (common.CACHING && !common.INDIVIDUAL && state.business && state.business[common.STATE]) {
        if (action.payload.callback) {
          action.payload.callback({ success: true, data: state.business[common.STATE] });
        }
        // make empty action call for skip
        return of(new globalActions.EmptyAction({}));
      } else if (common.CACHING && common.INDIVIDUAL && state.business &&
        state.business[common.STATE] &&
        state.business[common.STATE][action.payload.data[common.UNIQUE_KEY]]) {

        // data send from redux store if already available
        if (action.payload.callback) {
          action.payload.callback({ success: true, data: state.business[common.STATE][action.payload.data[common.UNIQUE_KEY]] });
        }

        return of(new globalActions.EmptyAction({}));
      }
      // Only sending payload data

      return this.businessService[common.API_METHOD](action.payload.data)
        .pipe(

          mergeMap((data: any) => {
            // throwing an error on if the status is not 200
            let actions: any[] = [];

            // dispatch business success action
            actions = [new businessActions.BusinessCommonSuccessAction({ data, common })];

            if (common.SUCCESS_NOTIFICATION) {
              // dispatch popup notification for open success popup
              actions.push(new globalActions.PopupNotificationAction({
                actionType: businessActions.ActionTypes.BUSINESS_COMMON, type: ModalType.SUCCESS,
                callback: action.payload.callback, title: common.TITLE, message: common.SUCCESS,
                code: `${common.SUCCESS}`, response: data,
              }));
            } else {
              action.payload.callback({ success: true, data });
            }

            return actions;
          }),
          catchError(error => {
            error.error = error.error || {};
            // dispatch popup notification for open error popup
            return of(new globalActions.PopupNotificationAction({
              actionType: businessActions.ActionTypes.BUSINESS_COMMON, type: ModalType.ERROR,
              message: error && error.error && error.error.message, callback: action.payload.callback, error: (error || error.error),
              // code: `ERROR.${error.code || error.error.code}`,
              title: common.TITLE,
            }))
          })
        );
    }),
  ));


}
